import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown, faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';

import { DropdownMenuItemComponent } from '../dropdown-menu/dropdown-menu-item/dropdown-menu-item.component';
import { DropdownMenuComponent } from '../dropdown-menu/dropdown-menu.component';

export interface DropdownOption<T = any> {
  label: string;
  disabled?: boolean;
  children?: DropdownOptionTree<T>;
  value?: T;
  icon?: IconProp;
}

export type DropdownOptionTree<T = any> = DropdownOption<T>[];

@Component({
  selector: 'rcp-dynamic-dropdown-menu',
  templateUrl: './dynamic-dropdown-menu.component.html',
  styles: `
    @use 'variables' as *;
    @use 'mixins' as *;

    :host {
      position: relative;
      display: inline-block;
      /* no z-index! */
    }
  `,
  imports: [DropdownMenuComponent, DropdownMenuItemComponent, FontAwesomeModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DynamicDropdownMenuComponent implements OnChanges {
  @Input() label: string;
  @Input() icon: IconProp = faAngleDown;
  @Input() color: 'primary' | 'positive' | 'negative' | undefined;
  @Input() options: DropdownOptionTree;
  @Input() disabled: boolean;
  @Input() immediateActionIfOneOption: boolean;

  @Input() menuPosition: 'above' | 'below' = 'below';

  @Output() clickOption: EventEmitter<DropdownOption> = new EventEmitter();

  activeOptionTree: DropdownOptionTree;
  _options: DropdownOptionTree;

  @ViewChild(DropdownMenuComponent)
  dropdownMenu: DropdownMenuComponent;

  protected readonly faAngleLeft = faAngleLeft;
  protected readonly faAngleRight = faAngleRight;

  ngOnChanges() {
    // if there is only one item which has sub-items, do not show the top level
    if (this.options?.length === 1 && this.options[0].children) {
      this._options = this.options[0].children;
    } else {
      this._options = this.options;
    }

    // set the root tree as the active tree
    this.activeOptionTree = this._options;
  }

  setActiveOptionTree(optionTree: DropdownOptionTree) {
    this.activeOptionTree = optionTree;
  }

  clickSelectorOption(option: DropdownOption, $event?: MouseEvent) {
    $event?.stopImmediatePropagation();

    if (option.disabled) {
      return;
    }

    if (option.children) {
      this.setActiveOptionTree(option.children);
    } else {
      this.clickOption.emit(option);
    }
  }

  // show form type select modal
  show() {
    if (this._options && this._options.length === 1 && !this._options[0].children && this.immediateActionIfOneOption) {
      // if there's exactly one option, perform click action
      this.clickSelectorOption(this._options[0]);
    } else {
      // set the root tree as the active tree
      // the dropdown is opened by rcp-dropdown-menu-component
      this.activeOptionTree = this._options;
    }
  }
}
